import { Controller } from "@hotwired/stimulus"

// TypeScript declaration for Turbo global
declare global {
  interface Window {
    Turbo?: {
      visit: (url: string, options?: object) => void
      renderStreamMessage: (html: string) => void
    }
  }
}

/**
 * Manages the visibility toggle between "Create Group" button and form section.
 * Hidden by default, shows form when button is clicked.
 */
export default class extends Controller {
  static targets = ["createButton", "formSection"]
  static values = { cancelUrl: String, ballotDesignUrl: String }

  declare createButtonTarget: HTMLElement
  declare formSectionTarget: HTMLElement
  declare hasCreateButtonTarget: boolean
  declare hasFormSectionTarget: boolean
  declare cancelUrlValue: string
  declare hasCancelUrlValue: boolean
  declare ballotDesignUrlValue: string
  declare hasBallotDesignUrlValue: boolean

  private boundHandleStreamRender: ((event: Event) => void) | null = null
  private boundHandleUnsavedChangesSaving: ((event: Event) => void) | null = null
  private boundHandleTurboSubmitEnd: ((event: Event) => void) | null = null
  private pendingEditUrl: string | null = null
  private pendingIsTurboStream: boolean = false
  private pendingFormElement: HTMLFormElement | null = null

  connect(): void {
    // Listen for turbo stream updates to detect when form section is replaced (e.g., edit action)
    this.boundHandleStreamRender = this.handleStreamRender.bind(this)
    document.addEventListener("turbo:before-stream-render", this.boundHandleStreamRender)

    // Listen for UnsavedChangesModal saving event so we can handle post-save turbo-stream
    // navigation after the modal controller gets disconnected by the save response
    this.boundHandleUnsavedChangesSaving = this.handleUnsavedChangesSaving.bind(this)
    document.addEventListener("unsaved-changes:saving", this.boundHandleUnsavedChangesSaving)

    // Listen for form submission end to navigate to pending edit URL on success
    this.boundHandleTurboSubmitEnd = this.handleTurboSubmitEnd.bind(this)
    document.addEventListener("turbo:submit-end", this.boundHandleTurboSubmitEnd)

    // Initial state: check if editing mode or auto-expand mode
    this.updateVisibility()
  }

  disconnect(): void {
    if (this.boundHandleStreamRender) {
      document.removeEventListener("turbo:before-stream-render", this.boundHandleStreamRender)
      this.boundHandleStreamRender = null
    }
    if (this.boundHandleUnsavedChangesSaving) {
      document.removeEventListener("unsaved-changes:saving", this.boundHandleUnsavedChangesSaving)
      this.boundHandleUnsavedChangesSaving = null
    }
    if (this.boundHandleTurboSubmitEnd) {
      document.removeEventListener("turbo:submit-end", this.boundHandleTurboSubmitEnd)
      this.boundHandleTurboSubmitEnd = null
    }
  }

  handleStreamRender(event: Event): void {
    const customEvent = event as CustomEvent
    const target = customEvent.target as HTMLElement
    
    // Check if this stream update is replacing the form section
    // If so, we need to ensure any existing modal controllers properly clean up
    if (target && target.getAttribute && target.getAttribute("target") === "choice-group-form") {
      // Dispatch a custom event to signal that form is being replaced
      // This allows the UnsavedChangesModal to clean itself up before DOM replacement
      document.dispatchEvent(new CustomEvent("choice-group-form:replacing", { bubbles: true }))
    }
    
    // After turbo stream updates, check visibility state
    setTimeout(() => this.updateVisibility(), 0)
  }

  updateVisibility(): void {
    if (!this.hasFormSectionTarget) return

    const isEditing = this.formSectionTarget.dataset.editing === "true"
    const autoExpand = this.formSectionTarget.dataset.autoExpand === "true"
    
    if (isEditing || autoExpand) {
      this.showForm()
    }
  }

  showForm(): void {
    if (this.hasCreateButtonTarget) {
      this.createButtonTarget.classList.add("hidden")
    }
    if (this.hasFormSectionTarget) {
      this.formSectionTarget.classList.remove("hidden")
    }
  }

  hideForm(): void {
    // Don't hide form if in auto-expand mode (no groups exist)
    const autoExpand = this.hasFormSectionTarget && this.formSectionTarget.dataset.autoExpand === "true"
    if (autoExpand) {
      // Just reset editing state, keep form visible
      if (this.hasFormSectionTarget) {
        this.formSectionTarget.dataset.editing = "false"
      }
      return
    }

    if (this.hasCreateButtonTarget) {
      this.createButtonTarget.classList.remove("hidden")
    }
    if (this.hasFormSectionTarget) {
      this.formSectionTarget.classList.add("hidden")
      // Reset editing state
      this.formSectionTarget.dataset.editing = "false"
    }
  }

  backToBallotDesign(event: Event): void {
    event.preventDefault()

    const url = this.ballotDesignUrlValue
    const formVisible = this.hasFormSectionTarget && !this.formSectionTarget.classList.contains('hidden')

    if (formVisible) {
      // Let the UnsavedChangesModal controller intercept and decide whether to show
      document.dispatchEvent(new CustomEvent('unsaved-changes:navigate', {
        detail: { url },
        bubbles: true
      }))
    } else {
      if (typeof window.Turbo !== 'undefined' && window.Turbo.visit) {
        window.Turbo.visit(url)
      } else {
        window.location.href = url
      }
    }
  }

  /**
   * Handle click on a non-editing item card during editing mode.
   * Delegates dirty-form detection to UnsavedChangesModal via unsaved-changes:navigate event.
   * If form is clean, modal fetches the edit URL as turbo-stream.
   * If form is dirty, modal shows the confirmation dialog.
   * 
   * Note: The data-action is on the turbo-frame element, not the article.
   */
  selectItem(event: Event): void {
    const element = event.currentTarget as HTMLElement
    const editUrl = element.dataset.editUrl
    if (!editUrl) return

    // Prevent any default link behavior (though turbo-frame shouldn't have links)
    event.preventDefault()

    document.dispatchEvent(new CustomEvent("unsaved-changes:navigate", {
      detail: { url: editUrl, turboStream: true },
      bubbles: true
    }))
  }

  /**
   * Capture pending navigation state before UnsavedChangesModal submits the form.
   * The modal controller may get disconnected when the save response replaces the form section,
   * so the management controller (which survives) handles the post-save navigation.
   */
  private handleUnsavedChangesSaving(event: Event): void {
    const customEvent = event as CustomEvent<{ url: string | null; turboStream: boolean }>
    const detail = customEvent.detail

    // Only intercept turbo-stream navigations (e.g., click-to-switch)
    if (!detail?.turboStream || !detail?.url) return

    this.pendingEditUrl = detail.url
    this.pendingIsTurboStream = true

    // Cache form element reference now — after DOM replacement it may no longer be findable
    this.pendingFormElement = this.hasFormSectionTarget
      ? (this.formSectionTarget.querySelector("form") as HTMLFormElement | null)
      : null
  }

  /**
   * After a successful form save, navigate to the pending edit URL as a turbo-stream.
   * This fires after the save response is processed (UnsavedChangesModal may already
   * be disconnected at this point if the form section was replaced).
   */
  private handleTurboSubmitEnd(event: Event): void {
    if (!this.pendingEditUrl || !this.pendingIsTurboStream) return

    const submitEvent = event as CustomEvent<{
      success: boolean
      formSubmission: { formElement: HTMLFormElement }
    }>

    // Only handle events from the form we captured
    if (this.pendingFormElement &&
        submitEvent.detail?.formSubmission?.formElement !== this.pendingFormElement) {
      return
    }

    const url = this.pendingEditUrl

    // Always clear pending state regardless of outcome
    this.pendingEditUrl = null
    this.pendingIsTurboStream = false
    this.pendingFormElement = null

    if (!submitEvent.detail?.success) {
      // Validation failed — stay on current item, server errors are already visible
      return
    }

    // Success — load clicked item's edit form via turbo-stream
    fetch(url, {
      method: "GET",
      headers: {
        "Accept": "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
      },
      credentials: "same-origin"
    })
      .then(response => response.text())
      .then(html => window.Turbo?.renderStreamMessage(html))
      .catch(error => console.error("Failed to fetch turbo stream after save:", error))
  }

  cancel(event: Event): void {
    event.preventDefault()

    // Immediate client-side feedback: hide form, restore create button
    this.hideForm()

    // Server-side: re-render the list to clear editing badges and restore action buttons
    if (!this.hasCancelUrlValue) return

    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''

    fetch(this.cancelUrlValue, {
      method: 'POST',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': csrfToken,
      },
    })
      .then(response => response.text())
      .then(html => window.Turbo?.renderStreamMessage(html))
      .catch(() => {
        // hideForm() already reset client-side state; silently ignore network errors
      })
  }
}
